import { useState, useEffect } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import Select, { components, type OptionProps } from "react-select";
import Swal from "sweetalert2";
import Modal from "../../../../../../components/base/Modal";
import type { Unit, UnitDto } from "../../../../../../store/admin/unitsStore";
import type { ThemeClasses } from "../../../../../../types/components";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../../../../utils/getDate";
import { getCustomSelectStyles } from "../../../../shared";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useSchemaOverrides } from "../../../../../../hooks/useSchemaOverrides";
import { usePolicyAccess } from "../../../../../../hooks/usePolicyAccess";
import { brand } from "../../../../../../config/theme";
import { MAX_FUEL_CAPACITY_L, isValidFuelCapacity, toFuelCapacity } from "../fuelCapacity";

const CREATE_STATUS_VALUE = "__create_new_status__";

interface EditUnitModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  unit: Unit;
  form: UseFormReturn<UnitDto>;
  types: { value: string; label: string }[];
  fuelTypes: { value: string; label: string }[];
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (data: UnitDto) => void;
}

export default function EditUnitModal({
  themeClasses,
  isDarkMode,
  unit,
  form,
  types,
  fuelTypes,
  loadingSubmit,
  onClose,
  onSubmit,
}: EditUnitModalProps) {
  const { t } = useTranslation("adminSections");
  const { isVisible, isRequired } = useSchemaOverrides("vehicles", "putVehicle");
  const { canEditPolicy } = usePolicyAccess();
  const [showCreateStatusModal, setShowCreateStatusModal] = useState(false);
  const [newStatusName, setNewStatusName] = useState("");
  const [creatingStatus, setCreatingStatus] = useState(false);
  const [createStatusError, setCreateStatusError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = form;

  const inputClassName = `w-full text-sm px-4 py-3 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`;

  const { statuses, createStatus, fetchStatuses } = useStatusesStore();
  // Para resellers usar la compañía seleccionada, para carriers usar su propia compañía
  const selectedCompanyId = useCompaniesStore((state) => state.companyId);
  const userCompanyId = localStorage.getItem("userCompanyId");
  const companyId = selectedCompanyId || userCompanyId;
  const baseStatusOptions = getStatusOptionsForEntity(statuses, "units", t, companyId);

  // Cargar statuses cuando el modal se abre
  useEffect(() => {
    if (statuses.length === 0) {
      fetchStatuses({ limit: 100, page: 1 });
    }
  }, [statuses.length, fetchStatuses]);

  // Agregar opción para crear nuevo estado
  const statusOptions = [
    ...baseStatusOptions,
    { value: CREATE_STATUS_VALUE, label: `+ ${t("units.form.createNewStatus", { defaultValue: "Crear nuevo estado" })}` },
  ];

  const handleStatusChange = (option: any, fieldOnChange: (value: string) => void) => {
    if (option?.value === CREATE_STATUS_VALUE) {
      setShowCreateStatusModal(true);
    } else {
      fieldOnChange(option?.value);
    }
  };

  const handleCreateStatus = async () => {
    if (!newStatusName.trim()) {
      setCreateStatusError(t("units.form.statusNameRequired", { defaultValue: "El nombre del estado es requerido" }));
      return;
    }

    setCreatingStatus(true);
    setCreateStatusError(null);

    try {
      const result = await createStatus({
        name: newStatusName.trim(),
        active: true,
        company_id: companyId,
      });

      if (result.success && result.data) {
        const newStatusId = result.data.id;
        await fetchStatuses({ limit: 100, page: 1 });
        setShowCreateStatusModal(false);
        setNewStatusName("");
        // Usar setTimeout para asegurar que el estado se actualice después del re-render
        setTimeout(() => {
          setValue("status_id", newStatusId);
        }, 100);
        Swal.fire({
          title: t("units.form.success", { defaultValue: "Éxito" }),
          text: t("units.form.statusCreatedSuccess", { defaultValue: "Estado creado correctamente" }),
          icon: "success",
          confirmButtonText: t("units.form.understood", { defaultValue: "Entendido" }),
          confirmButtonColor: brand.primary,
        });
      } else {
        setCreateStatusError(result.error || t("units.form.createStatusError", { defaultValue: "Error al crear el estado" }));
      }
    } catch (err) {
      setCreateStatusError(t("units.form.createStatusError", { defaultValue: "Error al crear el estado" }));
    } finally {
      setCreatingStatus(false);
    }
  };

  // Custom Option component para estilizar la opción de crear
  const CustomOption = (props: OptionProps<any>) => {
    const isCreateOption = props.data.value === CREATE_STATUS_VALUE;
    return (
      <components.Option {...props}>
        <span className={isCreateOption ? "text-primary-500 font-medium" : ""}>
          {props.data.label}
        </span>
      </components.Option>
    );
  };

  return (
    <Modal
      themeClasses={themeClasses}
      onClose={onClose}
      title={t("units.editTitle")}
      content={
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border border-gray-200 rounded-md p-4">
            <div>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>ID</p>
              <p className={`${themeClasses.text} font-mono break-all`}>
                {unit.id || "VEH-001"}
              </p>
            </div>
            <div>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>
                {t("units.fields.createdAt")}
              </p>
              <p className={`text-sm ${themeClasses.text}`}>
                {formatDate(unit.createdAt._seconds)}
              </p>
            </div>
            <div>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>
                {t("units.fields.updatedAt")}
              </p>
              <p className={`text-sm ${themeClasses.text}`}>
                {formatDate(unit.updatedAt._seconds)}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isVisible("name") && (
              <div className="flex flex-col gap-2">
                <label
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.name")} {isRequired("name") && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  defaultValue={unit.name}
                  placeholder={t("units.form.namePlaceholder")}
                  {...register("name", {
                    required: isRequired("name") ? t("units.form.requiredField") : false,
                    minLength: {
                      value: 3,
                      message: t("units.form.minLength3"),
                    },
                  })}
                  className={`${inputClassName} ${errors.name ? "ring-2 ring-red-500" : ""}`}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs">{errors.name.message}</p>
                )}
              </div>
              )}

              {isVisible("plate") && (
              <div className="flex flex-col gap-2">
                <label
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.plate")} {isRequired("plate") && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  defaultValue={unit.plate}
                  placeholder={t("units.form.platePlaceholder")}
                  {...register("plate", {
                    required: isRequired("plate") ? t("units.form.requiredField") : false,
                    minLength: {
                      value: 6,
                      message: t("units.form.minLength6"),
                    },
                    maxLength: {
                      value: 8,
                      message: t("units.form.maxLength"),
                    },
                  })}
                  className={`${inputClassName} ${errors.plate ? "ring-2 ring-red-500" : ""}`}
                />
                {errors.plate && (
                  <p className="text-red-500 text-xs">{errors.plate.message}</p>
                )}
              </div>
              )}
            </div>

            {/* Fila 2: Marca, Modelo y Tipo */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {isVisible("brand") && (
              <div className="flex flex-col gap-2">
                <label
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.brand")} {isRequired("brand") && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  defaultValue={unit.brand}
                  placeholder={t("units.form.brandPlaceholder")}
                  {...register("brand", {
                    required: isRequired("brand") ? t("units.form.requiredField") : false,
                    minLength: {
                      value: 3,
                      message: t("units.form.minLength3"),
                    },
                  })}
                  className={`${inputClassName} ${errors.brand ? "ring-2 ring-red-500" : ""}`}
                />
                {errors.brand && (
                  <p className="text-red-500 text-xs">{errors.brand.message}</p>
                )}
              </div>
              )}

              {isVisible("model") && (
              <div className="flex flex-col gap-2">
                <label
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.model")} {isRequired("model") && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  defaultValue={unit.model}
                  placeholder={t("units.form.modelPlaceholder")}
                  {...register("model", {
                    required: isRequired("model") ? t("units.form.requiredField") : false,
                    minLength: {
                      value: 3,
                      message: t("units.form.minLength3"),
                    },
                  })}
                  className={`${inputClassName} ${errors.model ? "ring-2 ring-red-500" : ""}`}
                />
                {errors.model && (
                  <p className="text-red-500 text-xs">{errors.model.message}</p>
                )}
              </div>
              )}

              {isVisible("type") && (
              <div className="flex flex-col gap-2">
                <label
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.type")} {isRequired("type") && <span className="text-red-500">*</span>}
                </label>
                <Controller
                  name="type"
                  control={control}
                  defaultValue={unit.type}
                  rules={{ required: isRequired("type") ? t("units.form.requiredField") : false }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={types.find(
                        (option) => option.value === field.value
                      )}
                      onChange={(option: any) => setValue("type", option.value)}
                      options={types}
                      isSearchable
                      placeholder={t("units.form.selectType")}
                      noOptionsMessage={() => null}
                      styles={getCustomSelectStyles(isDarkMode, !!errors.type)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                {errors.type && (
                  <p className="text-red-500 text-xs">
                    {t(errors.type?.message || "")}
                  </p>
                )}
              </div>
              )}
            </div>

            {/* Fila 3: Año y Tipo de Combustible */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isVisible("year") && (
              <div className="flex flex-col gap-2">
                <label
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.year")} {isRequired("year") && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  defaultValue={unit.year ?? ""}
                  placeholder={t("units.form.yearPlaceholder")}
                  {...register("year", {
                    required: isRequired("year") ? t("units.form.requiredField") : false,
                    setValueAs: (v: any) =>
                      v === "" || v === null || v === undefined ? undefined : Number(v),
                    min: {
                      value: 1900,
                      message: t("units.form.yearMin"),
                    },
                    max: {
                      value: new Date().getFullYear(),
                      message: t("units.form.yearMax"),
                    },
                  })}
                  className={`${inputClassName} ${errors.year ? "ring-2 ring-red-500" : ""}`}
                />
                {errors.year && (
                  <p className="text-red-500 text-xs">{errors.year.message}</p>
                )}
              </div>
              )}

              {isVisible("fuelType") && (
              <div className="flex flex-col gap-2">
                <label
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.fuelType")} {isRequired("fuelType") && <span className="text-red-500">*</span>}
                </label>
                <Controller
                  name="fuelType"
                  control={control}
                  defaultValue={unit.fuelType}
                  rules={{ required: isRequired("fuelType") ? t("units.form.requiredField") : false }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={fuelTypes.find(
                        (option) => option.value === field.value
                      )}
                      onChange={(option: any) =>
                        setValue("fuelType", option.value)
                      }
                      options={fuelTypes}
                      isSearchable
                      placeholder={t("units.form.selectFuelType")}
                      noOptionsMessage={() => null}
                      styles={getCustomSelectStyles(isDarkMode, !!errors.fuelType)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                {errors.fuelType && (
                  <p className="text-red-500 text-xs">
                    {t(errors.fuelType?.message || "")}
                  </p>
                )}
              </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isVisible("fuelCapacity") && (
              <div className="flex flex-col gap-2 w-full">
                <label htmlFor="fuelCapacity" className={`text-sm font-medium ${themeClasses.text}`}>
                  {t("units.fields.fuelCapacity")}
                </label>
                <input
                  id="fuelCapacity"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max={MAX_FUEL_CAPACITY_L}
                  step="any"
                  {...register("fuelCapacity", {
                    setValueAs: toFuelCapacity,
                    validate: (v) => isValidFuelCapacity(v) || t("units.form.fuelCapacityInvalid"),
                  })}
                  placeholder={t("units.form.fuelCapacityPlaceholder")}
                  className={`${inputClassName} ${errors.fuelCapacity ? "ring-2 ring-red-500" : ""}`}
                />
                {errors.fuelCapacity && <p className="text-red-500 text-xs">{errors.fuelCapacity.message}</p>}
              </div>
              )}

              {isVisible("status_id") && (
              <div className="flex flex-col gap-2">
                <label
                  className={`text-sm font-medium ${themeClasses.text}`}
                >
                  {t("units.fields.status")} {isRequired("status_id") && <span className="text-red-500">*</span>}
                </label>
                <Controller
                  name="status_id"
                  control={control}
                  defaultValue={unit.status_id}
                  rules={{ required: isRequired("status_id") ? t("units.form.requiredField") : false }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={baseStatusOptions.find(
                        (option) => option.value === field.value
                      )}
                      onChange={(option: any) => handleStatusChange(option, (val) => setValue("status_id", val))}
                      options={statusOptions}
                      components={{ Option: CustomOption }}
                      isSearchable
                      placeholder={t("units.form.selectStatus")}
                      noOptionsMessage={() => t("units.form.noStatusesAvailable")}
                      styles={getCustomSelectStyles(isDarkMode, !!errors.status_id)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                {errors.status_id && (
                  <p className="text-red-500 text-xs">
                    {t(errors.status_id?.message || "")}
                  </p>
                )}
              </div>
              )}
            </div>

            {/* Campos de Póliza */}
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {isVisible("policy_id") && (
                <div className="flex flex-col gap-2">
                  <label
                    className={`text-sm font-medium ${themeClasses.text}`}
                  >
                    {t("units.fields.policyId")} {isRequired("policy_id") && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    disabled={!canEditPolicy}
                    defaultValue={unit.policy_id}
                    placeholder={t("units.form.policyIdPlaceholder")}
                    {...register("policy_id", {
                      required: isRequired("policy_id") ? t("units.form.requiredField") : false,
                    })}
                    className={`${inputClassName} ${errors.policy_id ? "ring-2 ring-red-500" : ""} ${!canEditPolicy ? "opacity-60 cursor-not-allowed" : ""}`}
                  />
                  {errors.policy_id && (
                    <p className="text-red-500 text-xs">{errors.policy_id.message}</p>
                  )}
                </div>
                )}

                {isVisible("policy_expiry_date") && (
                <div className="flex flex-col gap-2">
                  <label
                    className={`text-sm font-medium ${themeClasses.text}`}
                  >
                    {t("units.fields.policyExpiryDate")} {isRequired("policy_expiry_date") && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="date"
                    disabled={!canEditPolicy}
                    defaultValue={unit.policy_expiry_date}
                    {...register("policy_expiry_date", {
                      required: isRequired("policy_expiry_date") ? t("units.form.requiredField") : false,
                    })}
                    className={`${inputClassName} ${errors.policy_expiry_date ? "ring-2 ring-red-500" : ""} ${!canEditPolicy ? "opacity-60 cursor-not-allowed" : ""}`}
                  />
                  {errors.policy_expiry_date && (
                    <p className="text-red-500 text-xs">{errors.policy_expiry_date.message}</p>
                  )}
                </div>
                )}
              </div>
              {!canEditPolicy && (
                <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} mt-2`}>
                  {t("units.form.policyEditInfo", { defaultValue: "Solo administradores pueden editar la información de póliza" })}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
            >
              {t("units.form.cancel")}
            </button>
            <button
              type="submit"
              disabled={loadingSubmit}
              className={`flex-1 py-3 px-4 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors ${
                loadingSubmit ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loadingSubmit
                ? t("units.form.submitting")
                : t("units.form.saveChanges")}
            </button>
          </div>

          {/* Modal para crear estado personalizado */}
          {showCreateStatusModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
              <div className={`${isDarkMode ? "bg-dark-bg" : "bg-white"} rounded-xl max-w-md w-full overflow-hidden border ${themeClasses.cardBorder} shadow-2xl`}>
                <div className={`flex items-center justify-between p-4 border-b ${themeClasses.cardBorder}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                      <i className="ri-add-line text-black text-lg"></i>
                    </div>
                    <h2 className={`font-semibold text-lg ${themeClasses.text}`}>
                      {t("units.form.createCustomStatus", { defaultValue: "Crear Estado Personalizado" })}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateStatusModal(false);
                      setNewStatusName("");
                      setCreateStatusError(null);
                    }}
                    className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
                  >
                    <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
                  </button>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <label className={`block text-sm ${themeClasses.text} mb-2`}>
                      {t("units.form.statusName", { defaultValue: "Nombre del estado" })} *
                    </label>
                    <input
                      type="text"
                      value={newStatusName}
                      onChange={(e) => setNewStatusName(e.target.value)}
                      placeholder={t("units.form.statusNamePlaceholder", { defaultValue: "Ej: En reparación" })}
                      className={inputClassName}
                      disabled={creatingStatus}
                    />
                  </div>

                  <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-4`}>
                    {t("units.form.customStatusInfo", { defaultValue: "Este estado personalizado estará disponible solo para tu compañía." })}
                  </p>

                  {createStatusError && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 mb-4">
                      <div className="flex items-start gap-2">
                        <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
                        <p className="text-sm text-red-500">{createStatusError}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateStatusModal(false);
                        setNewStatusName("");
                        setCreateStatusError(null);
                      }}
                      disabled={creatingStatus}
                      className={`px-6 py-2.5 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover} transition-colors font-medium`}
                    >
                      {t("units.form.cancel")}
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateStatus}
                      disabled={!newStatusName.trim() || creatingStatus}
                      className="px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {creatingStatus && <i className="ri-refresh-line animate-spin"></i>}
                      {creatingStatus ? t("units.form.creating", { defaultValue: "Creando..." }) : t("units.form.create", { defaultValue: "Crear" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      }
    />
  );
}

